import { useEffect, useState } from "react";
import { ParentGetAttendance, ParentGetBehaviors, ParentGetGrades, ParentGetGradesMonthlyProgress, ParentGetStudentPoints, parentGetSupervisorNotes, ParentGetTeacherEvaluations } from "services/Parent/ParentServices";
import ParentLoader from "./ParentLoader";
import { PARENT_STATUS_COLORS } from "constants/constants";
import { CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, PolarAngleAxis, PolarGrid, Radar, RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const ParentOverviewTab = ({ studentId, onNavigate, parentInfo }) => {
    const [allData, setAllData] = useState(null);
    const [hoveredCard, setHoveredCard] = useState(null);

    useEffect(() => {
        const fetchAll = async () => {

            const results = await Promise.allSettled([
                ParentGetAttendance(parentInfo?.token, Number(studentId)),
                ParentGetGrades(parentInfo?.token, Number(studentId)),
                ParentGetGradesMonthlyProgress(parentInfo?.token, Number(studentId)),
                ParentGetBehaviors(parentInfo?.token, Number(studentId)),
                ParentGetTeacherEvaluations(parentInfo?.token, Number(studentId)),
                parentGetSupervisorNotes(parentInfo?.token, Number(studentId)),
                ParentGetStudentPoints(parentInfo?.token, Number(studentId))
            ]);

            const data = {
                attendance: results[0].status === 'fulfilled' ? results[0].value.attendance : [],
                grades: results[1].status === 'fulfilled' ? results[1].value.grades : [],
                gradesMonthly: results[2].status === 'fulfilled' ? results[2].value.monthlyProgress : [],
                behaviors: results[3].status === 'fulfilled' ? results[3].value.behaviors : [],
                evaluations: results[4].status === 'fulfilled' ? results[4].value.evaluations : [],
                notes: results[5].status === 'fulfilled' ? results[5].value.notes : [],
                points: results[6].status === 'fulfilled' ? results[6].value : { points: 0, history: [] }
            };

            setAllData(data);

        };

        fetchAll();

    }, [studentId]);

    if (!allData) return <ParentLoader />;

    // these 5 should be filtered by month  filterByMonth();
    const attFiltered = allData.attendance

    const grdFiltered = allData.grades

    // filterByMonth(allData.behaviors, 'behavior_date')
    const behFiltered = allData.behaviors;

    const evaFiltered = allData.evaluations
    // filterByMonth(allData.notes, 'date');
    const ntsFiltered = allData.notes;

    // Stats Calculations

    const attCounts = { attend: 0, absent: 0, late: 0, 'left with parent': 0 };

    attFiltered.forEach(r => { attCounts[r.status] = (attCounts[r.status] || 0) + 1; });

    const attTotal = attFiltered.length || 1;

    const attPercent = Math.round((attCounts.attend / attTotal) * 100);



    const grdsAvg = grdFiltered.length ? Math.round(grdFiltered.reduce((s, g) => s + (g.result || 0), 0) / grdFiltered.length) : 0;



    const behTotal = behFiltered.length;

    const ntsPositive = ntsFiltered.filter(n => n.type === 'ايجابي').length;

    const ntsPercent = ntsFiltered.length ? Math.round((ntsPositive / ntsFiltered.length) * 100) : 0;



    const summaryCards = [

        { id: 'attendance', label: 'نسبة الحضور', value: `${attPercent}%`, icon: '📅', color: 'emerald', sub: `${attCounts.attend} يوم حاضر` },

        { id: 'grades', label: 'متوسط الدرجات', value: grdsAvg, icon: '📊', color: 'blue', sub: `من ${grdFiltered.length} اختبار` },

        { id: 'behaviors', label: 'السلوكيات', value: behTotal, icon: '📋', color: 'amber', sub: 'إجمالي السجلات' },

        { id: 'evaluations', label: 'التقييمات', value: evaFiltered.length, icon: '⭐', color: 'purple', sub: 'تقييمات المعلمين' },

        { id: 'notes', label: 'الروح المعنوية', value: `${ntsPercent}%`, icon: '📝', color: 'pink', sub: `${ntsPositive} ملاحظة إيجابية للطالب` },

        { id: 'points', label: 'النقاط', value: allData.points.points, icon: '🏆', color: 'indigo', sub: 'الرصيد الإجمالي' },

    ];

    const attChartData = [

        { name: 'حاضر', value: attCounts.attend, color: PARENT_STATUS_COLORS.attend },

        { name: 'غائب', value: attCounts.absent, color: PARENT_STATUS_COLORS.absent },

        { name: 'متأخر', value: attCounts.late, color: PARENT_STATUS_COLORS.late },

        { name: 'انصرف', value: attCounts['left with parent'], color: PARENT_STATUS_COLORS['left with parent'] },

    ].filter(d => d.value > 0);

    const radarData = [

        { subject: 'الحضور', A: attPercent, fullMark: 100 },

        { subject: 'الدرجات', A: grdsAvg, fullMark: 100 },

        { subject: 'السلوك', A: Math.max(0, 100 - (behTotal * 5)), fullMark: 100 },

        { subject: 'التقييم', A: evaFiltered.length ? Math.min(100, (evaFiltered[0].first_result + evaFiltered[0].second_result) * 5) : 0, fullMark: 100 },

    ];

    const notesChartData = [

        { name: 'إيجابي', value: ntsPositive, fill: '#10b981' },

        { name: 'سلبي', value: ntsFiltered.length - ntsPositive, fill: '#ef4444' },

    ].filter(d => d.value > 0);

    return (

        <div className="space-y-8 animate-in fade-in duration-500">

            <div className="flex gap-2">
                {/* Summary Cards */}
                <div className="w-1/2 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 py-10">
                    {summaryCards.map(card => (

                        <button

                            key={card.id}

                            onClick={() => onNavigate(card.id)}
                            onMouseEnter={() => setHoveredCard(card.id)}
                            onMouseLeave={() => setHoveredCard(null)}
                            className={`group bg-white/80 dark:bg-white/5 backdrop-blur-md border border-gray-200 dark:border-white/10 p-4 rounded-2xl text-right transition-all hover:shadow-lg hover:translate-y-[-4px] active:scale-95 flex flex-col justify-between h-32`}

                        >

                            <div className="flex items-start justify-between">

                                <span className={hoveredCard === card.id ? "text-4xl" : "text-2xl"}>{card.icon}</span>

                                <div className={`w-8 h-8 rounded-lg bg-${card.color}-500/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>

                                    <span className={`text-${card.color}-500 text-xs`}>→</span>

                                </div>

                            </div>

                            <div>

                                <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">{card.label}</div>

                                <div className={`text-2xl font-black text-${card.color}-600 dark:text-${card.color}-400`}>{card.value}</div>

                                <div className="text-[10px] font-medium text-gray-400 dark:text-gray-500 truncate">{card.sub}</div>

                            </div>

                        </button>

                    ))}
                </div>
                {/* Charts Row 1 */}
                <div className="w-1/2 flex flex-col gap-2">
                    <div className="bg-white/80 dark:bg-white/5 backdrop-blur-md border border-gray-200 dark:border-white/10 rounded-3xl p-6 shadow-sm">

                        <h4 className="font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">📊 توزيع الحضور <span className="text-xs font-normal text-gray-500">(بناءً على الفلتر)</span></h4>

                        <ResponsiveContainer width="100%" height={250}>

                            <PieChart>

                                <Pie data={attChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={8} dataKey="value">

                                    {attChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />)}

                                </Pie>

                                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />

                                <Legend verticalAlign="bottom" height={36} />

                            </PieChart>

                        </ResponsiveContainer>

                    </div>
                </div>
            </div>

            <div className="flex gap-2">
                <div className="w-1/2 bg-white/80 dark:bg-white/5 backdrop-blur-md border border-gray-200 dark:border-white/10 rounded-3xl p-6 shadow-sm">

                    <h4 className="font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">📝 تحليل ملاحظات الطالب</h4>

                    <ResponsiveContainer width="100%" height={240}>

                        <PieChart>

                            <Pie data={notesChartData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>

                                {notesChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}

                            </Pie>

                            <Tooltip />

                        </PieChart>

                    </ResponsiveContainer>

                    <div className="mt-4 flex justify-around text-xs font-bold">

                        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> إيجابية</div>

                        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500"></div> سلبية</div>

                    </div>

                </div>
                {/* diamond chart */}
                <div className="w-1/2 bg-white/80 dark:bg-white/5 backdrop-blur-md border border-gray-200 dark:border-white/10 rounded-3xl p-6 shadow-sm">

                    <h4 className="font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">🎯 مؤشر الكفاءة العام <span className="text-xs font-normal text-gray-500">(مؤشرات مركبة)</span></h4>

                    <ResponsiveContainer width="100%" height={250}>

                        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>

                            <PolarGrid stroke="currentColor" className="text-gray-200 dark:text-gray-700" />

                            <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 12 }} />

                            <Radar name="الأداء" dataKey="A" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} />

                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />

                        </RadarChart>

                    </ResponsiveContainer>

                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="lg:col-span-2 bg-white/80 dark:bg-white/5 backdrop-blur-md border border-gray-200 dark:border-white/10 rounded-3xl p-6 shadow-sm">

                <h4 className="font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">📈 تقدم الدرجات الشهري</h4>

                <ResponsiveContainer width="100%" height={240}>

                    <LineChart data={allData.gradesMonthly || []}>

                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-gray-100 dark:text-gray-800" />

                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={10} />

                        <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={10} />

                        <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />

                        <Legend />

                        <Line type="monotone" dataKey="avg_score" name="المتوسط" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', r: 4 }} />

                        <Line type="monotone" dataKey="max_score" name="الأعلى" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={false} />

                        <Line type="monotone" dataKey="min_score" name="الأدنى" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} />

                    </LineChart>

                </ResponsiveContainer>

            </div>

        </div>

    );

}

export default ParentOverviewTab;